import { useEffect, useMemo, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { Check, ChevronDown, ChevronUp, ChevronsUp, GitCommitHorizontal, Pencil, Plus, Send, Undo2 } from 'lucide-react'
import { BottomSheet } from '@/components/ui/BottomSheet'
import { Panel, ToneBadge, type Tone } from '@/components/ui/Tone'
import { useStudioStore } from '@/store'
import { useSuperAdmin } from '@/hooks/useSuperAdmin'
import {
  BRIEF_EXCEPTION_LABELS,
  BRIEF_QUESTIONS,
  STATUS_HINTS,
  STATUS_LABELS,
  STATUS_NEEDS_NOTE,
  STATUS_ORDER,
  activityLabel,
  buildBoardSections,
  daysSince,
  directionTags,
  formatDate,
  formatDateTime,
  moveInOrder,
  pluralDays,
  sectionFromParam,
  validateBrief,
  type BoardBlock,
  type PriorityStatus,
  type BriefException,
  type BriefQuestion,
  type BriefQuestionKey,
  type SectionKey,
  type WorkItemRow,
  type WorkItemStatus,
  type WorkRequestBrief,
} from '@/lib/workBoard'
import {
  addWorkItemComment,
  clearWorkItemBlock,
  createWorkItem,
  createWorkRequest,
  fetchWorkAcceptors,
  fetchWorkBoardMembers,
  fetchWorkDirections,
  fetchWorkItem,
  fetchWorkItems,
  isWorkBoardMember,
  markWorkItemNotificationsRead,
  reorderWorkItems,
  reviewWorkItemAcceptance,
  setWorkItemStatus,
  updateWorkItem,
  updateWorkRequest,
  type WorkAcceptor,
  type WorkDirection,
  type WorkItemDetail,
} from '@/services/workBoardService'

/**
 * «Запросы в разработку» — вкладки: работа (в работе + очередь по приоритету), новые запросы,
 * доработка ТЗ, приёмка, закрытые за срок. Вкладка и тег живут в URL
 * (`?tab=`, `?tag=`). Заказывает и видит всё любой сотрудник УК; порядок и карточки меняет
 * команда (`work_board_members`); запрос правят автор и принимающий, принимает — принимающий.
 * Гейты серверные, здесь только прячем кнопки.
 *
 * Карточки ведёт лид раз в неделю через навык `work-sync`; запрос заказчика — анкета по
 * правилам постановки (docs/WORK_REQUEST_RULES.md).
 */

const STATUS_TONE: Record<WorkItemStatus, Tone> = {
  inbox: 'info',
  queued: 'neutral',
  in_progress: 'accent',
  paused: 'warn',
  acceptance: 'success',
  done: 'success',
  dropped: 'neutral',
}

const DEFAULT_TAB: SectionKey = 'work'
const TAB_LABELS: Record<SectionKey, string> = {
  work: 'Работа',
  inbox: 'Новые',
  paused: 'Доработка ТЗ',
  acceptance: 'Приёмка',
  closed: 'Закрытые',
}

const CLOSED_WINDOWS = [
  { days: 30, label: '30 дней' },
  { days: 90, label: '90 дней' },
  { days: 365, label: 'год' },
]

const INPUT_CLASS =
  'h-9 w-full rounded-md border border-border bg-background px-3 text-sm outline-none focus:border-slate-400'
const TEXTAREA_CLASS =
  'w-full rounded-md border border-border bg-background p-2 text-sm outline-none focus:border-slate-400 disabled:opacity-50'
const PRIMARY_BUTTON =
  'inline-flex h-9 items-center gap-1.5 rounded-md bg-primary px-3 text-sm font-medium text-primary-foreground disabled:opacity-50'
const GHOST_BUTTON =
  'inline-flex h-8 items-center gap-1 rounded-md border border-border px-2 text-xs text-muted-foreground hover:bg-muted disabled:opacity-50'
const ICON_BUTTON =
  'inline-flex h-7 w-7 items-center justify-center rounded-md border border-border text-muted-foreground hover:bg-muted disabled:opacity-30'
const LABEL_CLASS = 'mb-1 block text-xs font-medium text-foreground'
const HINT_CLASS = 'mb-1 block text-xs text-muted-foreground'

function errorText(e: unknown, fallback: string): string {
  if (e instanceof Error && e.message) return e.message
  if (e && typeof e === 'object' && 'message' in e && typeof (e as { message: unknown }).message === 'string') {
    return (e as { message: string }).message
  }
  return fallback
}

function StatusBadge({ status }: { status: WorkItemStatus }) {
  return (
    <ToneBadge tone={STATUS_TONE[status]} emphasis={status === 'in_progress' ? 'strong' : 'soft'}>
      {STATUS_LABELS[status]}
    </ToneBadge>
  )
}

/**
 * Цвет направления (тега). Классы — литералами, чтобы их увидел Tailwind; лестницы
 * зеркалятся в тёмной теме, пара 100/800 читается в обеих. Новое направление без
 * цвета получает нейтральный.
 */
const DIRECTION_COLORS: Record<string, string> = {
  royalty: 'border-amber-200 bg-amber-100 text-amber-800',
  documents: 'border-stone-200 bg-stone-100 text-stone-800',
  original: 'border-teal-200 bg-teal-100 text-teal-800',
  closures: 'border-rose-200 bg-rose-100 text-rose-800',
  pedagogy: 'border-violet-200 bg-violet-100 text-violet-800',
  metrics: 'border-blue-200 bg-blue-100 text-blue-800',
  ratings: 'border-yellow-200 bg-yellow-100 text-yellow-800',
  managers: 'border-cyan-200 bg-cyan-100 text-cyan-800',
  clients: 'border-emerald-200 bg-emerald-100 text-emerald-800',
  marketing: 'border-pink-200 bg-pink-100 text-pink-800',
  crm: 'border-orange-200 bg-orange-100 text-orange-800',
  etl: 'border-lime-200 bg-lime-100 text-lime-800',
  access: 'border-red-200 bg-red-100 text-red-800',
  infra: 'border-indigo-200 bg-indigo-100 text-indigo-800',
  notifications: 'border-sky-200 bg-sky-100 text-sky-800',
  ui: 'border-fuchsia-200 bg-fuchsia-100 text-fuchsia-800',
  data_ops: 'border-purple-200 bg-purple-100 text-purple-800',
  team: 'border-green-200 bg-green-100 text-green-800',
}
const DIRECTION_FALLBACK = 'border-slate-200 bg-slate-100 text-slate-800'

function directionColor(code: string | null | undefined): string {
  return (code && DIRECTION_COLORS[code]) || DIRECTION_FALLBACK
}

function DirectionChip({ code, title }: { code: string; title: string }) {
  return (
    <span className={`shrink-0 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium ${directionColor(code)}`}>
      {title || 'Прочее'}
    </span>
  )
}

function ExceptionChip({ brief }: { brief: WorkRequestBrief | null }) {
  if (brief?.exception) return <ToneBadge tone="danger">{BRIEF_EXCEPTION_LABELS[brief.exception]}</ToneBadge>
  return null
}

/* ------------------------------------------------------------------ */
/* Строка списка                                                       */
/* ------------------------------------------------------------------ */

type Move = 'up' | 'down' | 'top'

function ItemRow({
  item,
  onOpen,
  position,
  onMove,
  canMove,
  moving,
}: {
  item: WorkItemRow
  onOpen: (id: number) => void
  position?: number
  onMove?: (move: Move) => void
  canMove?: { up: boolean; down: boolean }
  moving?: boolean
}) {
  const waiting = item.blocked_reason && !['done', 'dropped'].includes(item.status)
  const waitingDays = daysSince(item.blocked_since)
  const showNote = item.status === 'paused' || item.status === 'dropped'
  return (
    <div className="flex items-stretch gap-2 rounded-md border border-border bg-card">
      <button type="button" onClick={() => onOpen(item.id)} className="min-w-0 flex-1 p-3 text-left hover:bg-muted/40">
        <div className="flex items-start justify-between gap-2">
          <span className="min-w-0 flex-1 text-sm font-medium text-foreground">
            {position !== undefined && <span className="mr-1.5 text-muted-foreground">{position}.</span>}
            <span className="mr-1.5 font-normal tabular-nums text-muted-foreground">#{item.id}</span>
            {item.title}
          </span>
          <span className="flex shrink-0 items-center gap-1">
            {item.status === 'dropped' && <StatusBadge status={item.status} />}
            <DirectionChip code={item.direction_code} title={item.direction_title} />
          </span>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <ExceptionChip brief={item.request_brief} />
          {item.requester && <span>просит: {item.requester}</span>}
          {item.acceptor_name && <span>принимает: {item.acceptor_name}</span>}
          <span>{item.assignee_name ? `делает: ${item.assignee_name}` : 'исполнитель не назначен'}</span>
          <span>{item.closed_at ? `закрыто ${formatDate(item.closed_at)}` : activityLabel(item)}</span>
        </div>
        {waiting && (
          <p className="mt-1 text-xs text-muted-foreground">
            ⏳ Ждём{item.blocked_waiting_on ? ` (${item.blocked_waiting_on})` : ''}
            {waitingDays !== null && waitingDays > 0 ? `, ${waitingDays} ${pluralDays(waitingDays)}` : ''}: {item.blocked_reason}
          </p>
        )}
        {showNote && item.last_event_note && <p className="mt-1 text-xs text-muted-foreground">{item.last_event_note}</p>}
      </button>
      {onMove && canMove && (
        // Две стрелки вплотную, чтобы строка не росла; «в начало» — в открытой карточке.
        <div className="flex flex-col justify-center pr-1.5">
          <button type="button" className={`${ICON_BUTTON} rounded-b-none`} disabled={moving || !canMove.up} onClick={() => onMove('up')} aria-label="Выше" title="Выше">
            <ChevronUp size={14} />
          </button>
          <button type="button" className={`${ICON_BUTTON} -mt-px rounded-t-none`} disabled={moving || !canMove.down} onClick={() => onMove('down')} aria-label="Ниже" title="Ниже">
            <ChevronDown size={14} />
          </button>
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Запрос: анкета                                                      */
/* ------------------------------------------------------------------ */

type Answers = Partial<Record<BriefQuestionKey, string>>

interface RequestDraft {
  title: string
  details: string
  direction: string
  deadline: string
  acceptor: string
  exception: BriefException | ''
  answers: Answers
}

const EMPTY_DRAFT: RequestDraft = {
  title: '',
  details: '',
  direction: '',
  deadline: '',
  acceptor: '',
  exception: '',
  answers: {},
}

function draftFromDetail(detail: WorkItemDetail): RequestDraft {
  const { item } = detail
  const brief = item.request_brief ?? {}
  const answers: Answers = {}
  for (const q of BRIEF_QUESTIONS) answers[q.key] = brief[q.key] ?? ''
  return {
    title: item.title,
    details: item.request_text ?? '',
    direction: item.direction_code === 'other' ? '' : item.direction_code,
    deadline: brief.deadline ?? '',
    acceptor: item.acceptor_user_id ?? '',
    exception: brief.exception ?? '',
    answers,
  }
}

function briefFromDraft(d: RequestDraft): WorkRequestBrief {
  const brief: WorkRequestBrief = {}
  for (const q of BRIEF_QUESTIONS) {
    const answer = d.answers[q.key]?.trim()
    if (answer) brief[q.key] = answer
  }
  if (d.exception) brief.exception = d.exception
  if (d.deadline.trim()) brief.deadline = d.deadline.trim()
  return brief
}

function QuestionField({
  number,
  question,
  value,
  error,
  onChange,
}: {
  number: number
  question: BriefQuestion
  value: string
  error?: string
  onChange: (value: string) => void
}) {
  return (
    <div className="space-y-1">
      <span className={LABEL_CLASS}>
        {number}. {question.title}
      </span>
      <span className={HINT_CLASS}>{question.hint}</span>
      <textarea
        value={value}
        onChange={e => onChange(e.target.value)}
        rows={2}
        className={TEXTAREA_CLASS}
        placeholder={question.placeholder}
      />
      {error && <span className="text-xs text-red-600">{error}</span>}
    </div>
  )
}

function RequestSheet({
  open,
  editing,
  onClose,
  onSaved,
  directions,
  acceptors,
  briefOptional,
}: {
  open: boolean
  /** Правка существующего запроса; null — новый. */
  editing: WorkItemDetail | null
  onClose: () => void
  onSaved: (id: number, mode: 'created' | 'updated' | 'resubmitted') => void
  directions: WorkDirection[]
  acceptors: WorkAcceptor[]
  /** Супер-админ: анкета и принимающий необязательны (сервер пропускает так же). */
  briefOptional: boolean
}) {
  const [draft, setDraft] = useState<RequestDraft>(EMPTY_DRAFT)
  const [showWhy, setShowWhy] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<BriefQuestionKey | 'title' | 'acceptor', string>>>({})

  useEffect(() => {
    if (open) {
      setDraft(editing ? draftFromDetail(editing) : EMPTY_DRAFT)
      setError(null)
      setFieldErrors({})
    }
  }, [open, editing])

  const set = <K extends keyof RequestDraft>(key: K, value: RequestDraft[K]) => setDraft(d => ({ ...d, [key]: value }))
  const setAnswer = (key: BriefQuestionKey, value: string) => setDraft(d => ({ ...d, answers: { ...d.answers, [key]: value } }))

  const resubmit = editing?.item.status === 'paused'

  const submit = async () => {
    const brief = briefFromDraft(draft)
    const errors: typeof fieldErrors = briefOptional ? {} : { ...validateBrief(brief) }
    if (draft.title.trim().length < 3) errors.title = 'Назовите запрос хотя бы в нескольких словах'
    if (!draft.acceptor && !briefOptional) errors.acceptor = 'Выберите, кто принимает результат'
    setFieldErrors(errors)
    if (Object.keys(errors).length > 0) {
      setError('Заполнены не все поля — они отмечены ниже')
      return
    }
    setSaving(true)
    try {
      const input = {
        title: draft.title,
        requestText: draft.details,
        brief,
        acceptorUserId: draft.acceptor || null,
        directionCode: draft.direction || null,
      }
      if (editing) {
        await updateWorkRequest(editing.item.id, input, resubmit)
        onSaved(editing.item.id, resubmit ? 'resubmitted' : 'updated')
      } else {
        onSaved(await createWorkRequest(input), 'created')
      }
    } catch (e) {
      setError(errorText(e, 'Не удалось отправить запрос'))
    } finally {
      setSaving(false)
    }
  }

  const question = (key: BriefQuestionKey) => BRIEF_QUESTIONS.find(q => q.key === key) as BriefQuestion
  const field = (number: number, key: BriefQuestionKey) => (
    <QuestionField
      key={key}
      number={number}
      question={question(key)}
      value={draft.answers[key] ?? ''}
      error={fieldErrors[key]}
      onChange={v => setAnswer(key, v)}
    />
  )

  return (
    <BottomSheet
      isOpen={open}
      onClose={onClose}
      title={editing ? `Запрос #${editing.item.id}` : 'Попросить доработку'}
      className="sm:max-w-2xl"
    >
      <div className="space-y-4 p-4">
        <Panel size="sm">
          <p className="text-xs">
            Ответы нужны, чтобы не тратить разработку на то, что окажется невостребованным или не решит проблему. На
            каждый вопрос нужен короткий содержательный ответ.{' '}
            <button type="button" className="text-primary" onClick={() => setShowWhy(v => !v)}>
              {showWhy ? 'Свернуть' : 'Подробнее'}
            </button>
          </p>
          {briefOptional && (
            <p className="mt-2 text-xs font-medium">
              Вы супер-админ: достаточно названия. Анкету и принимающего можно заполнить частично или позже.
            </p>
          )}
          {showWhy && (
            <p className="mt-2 text-xs">
              Срочность сама по себе не заменяет ответов. Исключения — аварии, безопасность, юридические требования и
              внешние обязательства: для них достаточно описать проблему. Высокий приоритет нового запроса означает, что
              другая работа сдвинется. Через 30–60 дней после запуска проверяем, пользуются ли результатом и изменился ли
              показатель (вопрос 9).
            </p>
          )}
        </Panel>

        <div className="space-y-3">
          <label className="block">
            <span className={LABEL_CLASS}>Что нужно — одной фразой</span>
            <input
              value={draft.title}
              onChange={e => set('title', e.target.value)}
              className={INPUT_CLASS}
              placeholder="Сверка роялти с банковской выпиской"
              autoFocus={!editing}
            />
            {fieldErrors.title && <span className="text-xs text-red-600">{fieldErrors.title}</span>}
          </label>
          <label className="block">
            <span className={LABEL_CLASS}>Подробности (необязательно)</span>
            <textarea
              value={draft.details}
              onChange={e => set('details', e.target.value)}
              rows={3}
              className={TEXTAREA_CLASS}
              placeholder="Как сейчас устроено, ссылки на таблицы, примеры."
            />
          </label>
          <div className="grid gap-2 sm:grid-cols-2">
            <label className="block">
              <span className={LABEL_CLASS}>Тег</span>
              <select value={draft.direction} onChange={e => set('direction', e.target.value)} className={INPUT_CLASS}>
                <option value="">Без тега</option>
                {directions.filter(d => d.is_active && d.code !== 'other').map(d => (
                  <option key={d.code} value={d.code}>{d.title}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className={LABEL_CLASS}>Срок, если он реально есть</span>
              <input value={draft.deadline} onChange={e => set('deadline', e.target.value)} className={INPUT_CLASS} placeholder="до 15.10 — отчёт собственникам" />
            </label>
          </div>
          <label className="block">
            <span className={LABEL_CLASS}>Это срочное исключение?</span>
            <select value={draft.exception} onChange={e => set('exception', e.target.value as BriefException | '')} className={INPUT_CLASS}>
              <option value="">Нет, обычный запрос</option>
              {(Object.keys(BRIEF_EXCEPTION_LABELS) as BriefException[]).map(k => (
                <option key={k} value={k}>{BRIEF_EXCEPTION_LABELS[k]}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="space-y-4 border-t border-border pt-3">
          {field(1, 'problem')}
          {!draft.exception && (
            <>
              {field(2, 'users')}
              {field(3, 'frequency')}
              {field(4, 'action')}
              {field(5, 'business_result')}
              {field(6, 'evidence')}
              {field(7, 'cheaper_check')}
            </>
          )}
          <label className="block space-y-1">
            <span className={LABEL_CLASS}>{draft.exception ? 2 : 8}. Кто принимает результат со стороны бизнеса?</span>
            <span className={HINT_CLASS}>
              Отвечает не за то, чтобы «разработка сделала», а за то, чтобы результатом пользовались и он дал эффект. Этот
              человек проверит работу во вкладке «Приёмка». Выбор — среди сотрудников УК с расширенным доступом.
            </span>
            <select value={draft.acceptor} onChange={e => set('acceptor', e.target.value)} className={INPUT_CLASS}>
              <option value="">{briefOptional ? 'Не указан — принимает автор' : 'Выберите человека'}</option>
              {acceptors.map(a => (
                <option key={a.user_id} value={a.user_id}>{a.display_name ?? a.user_id}</option>
              ))}
            </select>
            {fieldErrors.acceptor && <span className="text-xs text-red-600">{fieldErrors.acceptor}</span>}
          </label>
          {!draft.exception && (
            <>
              {field(9, 'success_check')}
            </>
          )}
        </div>

        {error && <Panel tone="danger" size="sm">{error}</Panel>}
        <div className="flex justify-end">
          <button type="button" className={PRIMARY_BUTTON} disabled={saving} onClick={() => void submit()}>
            <Send size={14} /> {!editing ? 'Отправить' : resubmit ? 'Отправить снова' : 'Сохранить'}
          </button>
        </div>
      </div>
    </BottomSheet>
  )
}

/** Анкета в карточке: вопросы по порядку правил. */
function BriefView({ item }: { item: WorkItemDetail['item'] }) {
  const brief = item.request_brief
  if (!brief) return null
  const rows: { title: string; value: string | null }[] = []
  BRIEF_QUESTIONS.forEach(q => {
    if (brief.exception && q.key !== 'problem') return
    if (q.key === 'success_check') rows.push({ title: 'Кто принимает результат', value: item.acceptor_name })
    rows.push({ title: q.title, value: brief[q.key] ?? null })
  })
  if (brief.exception) rows.push({ title: 'Кто принимает результат', value: item.acceptor_name })
  return (
    <div className="space-y-2">
      {brief.exception && <Panel tone="danger" size="sm">Срочное исключение: {BRIEF_EXCEPTION_LABELS[brief.exception]}</Panel>}
      <dl className="space-y-2 rounded-md bg-muted/40 p-3 text-sm">
        {rows.map((r, i) => (
          <div key={r.title}>
            <dt className="text-xs text-muted-foreground">
              {i + 1}. {r.title}
            </dt>
            <dd className={`whitespace-pre-wrap ${r.value ? '' : 'text-muted-foreground'}`}>{r.value ?? 'Нет ответа'}</dd>
          </div>
        ))}
        {brief.deadline && (
          <div>
            <dt className="text-xs text-muted-foreground">Срок</dt>
            <dd>{brief.deadline}</dd>
          </div>
        )}
      </dl>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Новая карточка (команда)                                            */
/* ------------------------------------------------------------------ */

function NewItemSheet({
  open,
  onClose,
  onCreated,
  members,
  directions,
}: {
  open: boolean
  onClose: () => void
  onCreated: (id: number) => void
  members: { user_id: string; display_name: string | null }[]
  directions: WorkDirection[]
}) {
  const [title, setTitle] = useState('')
  const [text, setText] = useState('')
  const [assignee, setAssignee] = useState('')
  const [direction, setDirection] = useState('other')
  const [status, setStatus] = useState<WorkItemStatus>('in_progress')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setTitle('')
      setText('')
      setAssignee('')
      setDirection('other')
      setStatus('in_progress')
      setError(null)
    }
  }, [open])

  const submit = async () => {
    setSaving(true)
    try {
      const id = await createWorkItem({
        title,
        directionCode: direction,
        kind: 'feature',
        status,
        requestText: text,
        assigneeUserId: assignee || null,
      })
      onCreated(id)
    } catch (e) {
      setError(errorText(e, 'Не удалось создать карточку'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <BottomSheet isOpen={open} onClose={onClose} title="Новая карточка" className="sm:max-w-xl">
      <div className="space-y-3 p-4">
        <input value={title} onChange={e => setTitle(e.target.value)} className={INPUT_CLASS} placeholder="Результат одной фразой, понятной снаружи" autoFocus />
        <textarea value={text} onChange={e => setText(e.target.value)} rows={4} className={TEXTAREA_CLASS} placeholder="Подробности (необязательно)" />
        <div className="grid gap-2 sm:grid-cols-3">
          <select value={assignee} onChange={e => setAssignee(e.target.value)} className={INPUT_CLASS}>
            <option value="">Исполнитель не назначен</option>
            {members.map(m => (
              <option key={m.user_id} value={m.user_id}>{m.display_name ?? m.user_id}</option>
            ))}
          </select>
          <select value={direction} onChange={e => setDirection(e.target.value)} className={INPUT_CLASS} aria-label="Тег">
            {directions.filter(d => d.is_active).map(d => (
              <option key={d.code} value={d.code}>{d.title}</option>
            ))}
          </select>
          <select value={status} onChange={e => setStatus(e.target.value as WorkItemStatus)} className={INPUT_CLASS}>
            {STATUS_ORDER.filter(s => s !== 'dropped').map(s => (
              <option key={s} value={s}>{STATUS_LABELS[s]}</option>
            ))}
          </select>
        </div>
        {error && <Panel tone="danger" size="sm">{error}</Panel>}
        <div className="flex justify-end">
          <button type="button" className={PRIMARY_BUTTON} disabled={saving || title.trim().length < 3} onClick={() => void submit()}>
            <Plus size={14} /> Создать
          </button>
        </div>
      </div>
    </BottomSheet>
  )
}

/* ------------------------------------------------------------------ */
/* Карточка                                                            */
/* ------------------------------------------------------------------ */

const JOURNAL_EVENTS = new Set(['created', 'status', 'progress', 'comment', 'blocked', 'unblocked'])

function ItemSheet({
  itemId,
  isMember,
  members,
  directions,
  onClose,
  onChanged,
  onEditRequest,
  onMoveTop,
}: {
  itemId: number | null
  isMember: boolean
  members: { user_id: string; display_name: string | null }[]
  directions: WorkDirection[]
  onClose: () => void
  onChanged: () => void
  onEditRequest: (detail: WorkItemDetail) => void
  onMoveTop: (status: PriorityStatus, id: number) => Promise<void>
}) {
  const queryClient = useQueryClient()
  const { data, isLoading, error: loadError } = useQuery({
    queryKey: ['work-item', itemId],
    queryFn: () => fetchWorkItem(itemId as number),
    enabled: itemId !== null,
  })
  const item = data?.item

  const [nextStatus, setNextStatus] = useState<WorkItemStatus | ''>('')
  const [statusNote, setStatusNote] = useState('')
  const [reviewNote, setReviewNote] = useState('')
  const [comment, setComment] = useState('')
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState({ title: '', request_text: '', requester: '', assignee_user_id: '', direction_code: '' })
  const [showAllCommits, setShowAllCommits] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setNextStatus('')
    setStatusNote('')
    setReviewNote('')
    setComment('')
    setEditing(false)
    setShowAllCommits(false)
    setError(null)
  }, [itemId])

  useEffect(() => {
    if (itemId === null) return
    void markWorkItemNotificationsRead(itemId)
      .then(() => window.dispatchEvent(new Event('work-board-notifications-changed')))
      .catch(() => { /* Прочтение не должно мешать открыть карточку. */ })
  }, [itemId])

  const run = async (fn: () => Promise<unknown>, fallback: string) => {
    setBusy(true)
    try {
      await fn()
      setError(null)
      await queryClient.invalidateQueries({ queryKey: ['work-item', itemId] })
      onChanged()
      return true
    } catch (e) {
      setError(errorText(e, fallback))
      return false
    } finally {
      setBusy(false)
    }
  }

  const noteRequired = nextStatus !== '' && STATUS_NEEDS_NOTE.includes(nextStatus)
  const commits = (data?.links ?? []).filter(l => l.kind === 'commit')
  const otherLinks = (data?.links ?? []).filter(l => l.kind !== 'commit')
  const journal = (data?.events ?? []).filter(e => JOURNAL_EVENTS.has(e.event_type))
  /** Что попросили доработать: заметка последнего перевода в «Доработку». */
  const reworkNote = (data?.events ?? []).find(e => e.event_type === 'status' && e.payload.to === 'paused')?.note ?? null

  const describeEvent = (e: NonNullable<typeof data>['events'][number]) => {
    if (e.event_type === 'status') {
      const to = e.payload.to as WorkItemStatus | undefined
      const label = to && to in STATUS_LABELS ? STATUS_LABELS[to] : String(to ?? '')
      if (e.payload.acceptance) return `${to === 'done' ? 'Принято' : 'Возвращено в работу'}${e.note ? ` — ${e.note}` : ''}`
      return `Статус: ${label}${e.note ? ` — ${e.note}` : ''}`
    }
    if (e.event_type === 'blocked') return `Ждём: ${e.note ?? ''}`
    if (e.event_type === 'unblocked') return `Больше не ждём${e.note ? `: ${e.note}` : ''}`
    return e.note ?? ''
  }

  return (
    <BottomSheet isOpen={itemId !== null} onClose={onClose} title={item ? `#${item.id}` : 'Карточка'} className="sm:max-w-2xl">
      <div className="space-y-4 p-4">
        {isLoading && <p className="text-sm text-muted-foreground">Загрузка…</p>}
        {loadError && <Panel tone="danger" size="sm">{errorText(loadError, 'Не удалось открыть карточку')}</Panel>}

        {item && data && !editing && (
          <>
            <div>
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-base font-semibold text-foreground">{item.title}</h3>
                <StatusBadge status={item.status} />
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{STATUS_HINTS[item.status]}</p>
              <p className="mt-2 flex flex-wrap items-center gap-x-1 text-xs text-muted-foreground">
                <DirectionChip code={item.direction_code} title={item.direction_title} />
                <span>
                  {item.requester ? ` Просит: ${item.requester} · ` : ' '}
                  {item.acceptor_name ? `Принимает: ${item.acceptor_name} · ` : ''}
                  {item.assignee_name ? `Делает: ${item.assignee_name}` : 'Исполнитель не назначен'}
                  {` · заведено ${formatDate(item.created_at)}`}
                  {item.done_at ? ` · готово ${formatDate(item.done_at)}` : ''}
                </span>
              </p>
            </div>

            {item.status === 'paused' && data.can_edit_request && (
              <Panel tone="warn" size="sm">
                <p>Запрос вернули на доработку{reworkNote ? `: ${reworkNote}` : '.'}</p>
                <p className="mt-1">Дополните ответы и отправьте снова — запрос вернётся в «Новые».</p>
                <button type="button" className={`${PRIMARY_BUTTON} mt-2`} onClick={() => onEditRequest(data)}>
                  <Pencil size={14} /> Доработать запрос
                </button>
              </Panel>
            )}

            {data.can_review_acceptance && (
              <Panel tone="success" size="sm">
                <p className="font-medium">Проверьте результат</p>
                <p className="mt-1 text-xs">Принять — карточка закроется. Вернуть — команда получит замечание и поднимет карточку наверх «В работе».</p>
                <input
                  value={reviewNote}
                  onChange={e => setReviewNote(e.target.value)}
                  className={`${INPUT_CLASS} mt-2`}
                  placeholder="Замечание (обязательно, если возвращаете)"
                />
                <div className="mt-2 flex flex-wrap gap-2">
                  <button
                    type="button"
                    className={PRIMARY_BUTTON}
                    disabled={busy}
                    onClick={() => void run(() => reviewWorkItemAcceptance(item.id, true, reviewNote), 'Не удалось принять').then(ok => ok && setReviewNote(''))}
                  >
                    <Check size={14} /> Принять
                  </button>
                  <button
                    type="button"
                    className={GHOST_BUTTON}
                    disabled={busy || reviewNote.trim().length < 3}
                    onClick={() => void run(() => reviewWorkItemAcceptance(item.id, false, reviewNote), 'Не удалось вернуть').then(ok => ok && setReviewNote(''))}
                  >
                    <Undo2 size={12} /> Вернуть в работу
                  </button>
                </div>
              </Panel>
            )}

            {item.request_text && <p className="whitespace-pre-wrap rounded-md bg-muted/40 p-3 text-sm">{item.request_text}</p>}
            <BriefView item={item} />
            {data.can_edit_request && item.status !== 'paused' && (
              <button type="button" className={GHOST_BUTTON} onClick={() => onEditRequest(data)}>
                <Pencil size={12} /> {item.request_brief ? 'Изменить запрос' : 'Заполнить анкету запроса'}
              </button>
            )}

            {item.blocked_reason && (
              <Panel tone="warn" size="sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span>
                    Ждём{item.blocked_waiting_on ? ` (${item.blocked_waiting_on})` : ''}: {item.blocked_reason}
                  </span>
                  {isMember && (
                    <button type="button" className={GHOST_BUTTON} disabled={busy} onClick={() => void run(() => clearWorkItemBlock(item.id, null), 'Не удалось снять ожидание')}>
                      Больше не ждём
                    </button>
                  )}
                </div>
              </Panel>
            )}

            {isMember && (
              <div className="space-y-2 rounded-md border border-border p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={nextStatus}
                    onChange={e => setNextStatus(e.target.value as WorkItemStatus | '')}
                    className={`${INPUT_CLASS} w-auto`}
                    aria-label="Новый статус"
                  >
                    <option value="">Сменить статус…</option>
                    {STATUS_ORDER.filter(s => s !== item.status).map(s => (
                      <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className={GHOST_BUTTON}
                    onClick={() => {
                      setDraft({
                        title: item.title,
                        request_text: item.request_text ?? '',
                        requester: item.requester ?? '',
                        assignee_user_id: item.assignee_user_id ?? '',
                        direction_code: item.direction_code,
                      })
                      setEditing(true)
                    }}
                  >
                    <Pencil size={12} /> Изменить
                  </button>
                  {(item.status === 'in_progress' || item.status === 'queued') && (
                    <button
                      type="button"
                      className={GHOST_BUTTON}
                      disabled={busy || item.priority_rank === 1}
                      onClick={() => void run(() => onMoveTop(item.status as PriorityStatus, item.id), 'Не удалось поднять')}
                    >
                      <ChevronsUp size={12} /> В начало «{STATUS_LABELS[item.status]}»
                    </button>
                  )}
                </div>
                {nextStatus && (
                  <div className="flex flex-wrap gap-2">
                    <input
                      value={statusNote}
                      onChange={e => setStatusNote(e.target.value)}
                      className={`${INPUT_CLASS} flex-1`}
                      placeholder={
                        nextStatus === 'paused'
                          ? 'Что доработать заказчику в ТЗ? Он увидит эту фразу'
                          : noteRequired
                            ? 'Почему? Заказчик увидит эту фразу'
                            : 'Заметка (необязательно)'
                      }
                    />
                    <button
                      type="button"
                      className={PRIMARY_BUTTON}
                      disabled={busy || (noteRequired && statusNote.trim().length < 3)}
                      onClick={() =>
                        void run(() => setWorkItemStatus(item.id, nextStatus, statusNote), 'Не удалось сменить статус').then(ok => {
                          if (ok) {
                            setNextStatus('')
                            setStatusNote('')
                          }
                        })
                      }
                    >
                      Сохранить
                    </button>
                  </div>
                )}
              </div>
            )}

            <div>
              <h4 className="mb-1 text-sm font-medium">Журнал</h4>
              <ul className="space-y-1 text-sm">
                {journal.map(e => (
                  <li key={e.id} className="flex gap-2">
                    <span className="shrink-0 text-xs text-muted-foreground">{formatDateTime(e.occurred_at)}</span>
                    <span className="min-w-0">
                      {describeEvent(e)}
                      {e.actor_name && <span className="text-xs text-muted-foreground"> · {e.actor_name}</span>}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="mt-2 flex gap-2">
                <input value={comment} onChange={e => setComment(e.target.value)} className={`${INPUT_CLASS} flex-1`} placeholder="Дописать или спросить" />
                <button
                  type="button"
                  className={GHOST_BUTTON}
                  disabled={busy || comment.trim().length < 2}
                  onClick={() => void run(() => addWorkItemComment(item.id, comment), 'Не удалось отправить').then(ok => ok && setComment(''))}
                >
                  <Send size={12} />
                </button>
              </div>
            </div>

            {(commits.length > 0 || otherLinks.length > 0) && (
              <div>
                <h4 className="mb-1 text-sm font-medium">Коммиты и ссылки</h4>
                <ul className="space-y-0.5 text-xs text-muted-foreground">
                  {(showAllCommits ? commits : commits.slice(0, 8)).map(l => (
                    <li key={l.id} className="flex gap-2">
                      <GitCommitHorizontal size={12} className="mt-0.5 shrink-0" />
                      <span className="shrink-0 font-mono">{l.ref.slice(0, 8)}</span>
                      <span className="min-w-0 truncate">{l.title ?? ''}</span>
                      {l.occurred_at && <span className="shrink-0">{formatDate(l.occurred_at)}</span>}
                    </li>
                  ))}
                  {otherLinks.map(l => (
                    <li key={l.id}>{l.kind}: {l.title ?? l.ref}</li>
                  ))}
                </ul>
                {commits.length > 8 && !showAllCommits && (
                  <button type="button" className="mt-1 text-xs text-primary" onClick={() => setShowAllCommits(true)}>
                    показать все {commits.length}
                  </button>
                )}
              </div>
            )}
          </>
        )}

        {item && editing && (
          <div className="space-y-3">
            <input value={draft.title} onChange={e => setDraft({ ...draft, title: e.target.value })} className={INPUT_CLASS} aria-label="Название" />
            <textarea value={draft.request_text} onChange={e => setDraft({ ...draft, request_text: e.target.value })} rows={5} className={TEXTAREA_CLASS} aria-label="Что нужно" />
            <div className="grid gap-2 sm:grid-cols-3">
              <input value={draft.requester} onChange={e => setDraft({ ...draft, requester: e.target.value })} className={INPUT_CLASS} placeholder="Кто просит" />
              <select value={draft.assignee_user_id} onChange={e => setDraft({ ...draft, assignee_user_id: e.target.value })} className={INPUT_CLASS}>
                <option value="">Исполнитель не назначен</option>
                {members.map(m => (
                  <option key={m.user_id} value={m.user_id}>{m.display_name ?? m.user_id}</option>
                ))}
              </select>
              <select value={draft.direction_code} onChange={e => setDraft({ ...draft, direction_code: e.target.value })} className={INPUT_CLASS} aria-label="Тег">
                {directions.filter(d => d.is_active || d.code === draft.direction_code).map(d => (
                  <option key={d.code} value={d.code}>{d.title}</option>
                ))}
              </select>
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" className={GHOST_BUTTON} onClick={() => setEditing(false)}>Отмена</button>
              <button
                type="button"
                className={PRIMARY_BUTTON}
                disabled={busy || draft.title.trim().length < 3}
                onClick={() =>
                  void run(
                    () =>
                      updateWorkItem(item.id, {
                        title: draft.title.trim(),
                        request_text: draft.request_text.trim() || null,
                        requester: draft.requester.trim() || null,
                        assignee_user_id: draft.assignee_user_id || null,
                        direction_code: draft.direction_code,
                      }),
                    'Не удалось сохранить'
                  ).then(ok => ok && setEditing(false))
                }
              >
                Сохранить
              </button>
            </div>
          </div>
        )}

        {error && <Panel tone="danger" size="sm">{error}</Panel>}
      </div>
    </BottomSheet>
  )
}

/* ------------------------------------------------------------------ */
/* Экран                                                               */
/* ------------------------------------------------------------------ */

function toIsoDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

export const TeamWorkBoardPage: React.FC = () => {
  const queryClient = useQueryClient()
  const { studios } = useStudioStore()
  const isManagementCompany = studios.some(s => s.access_kind === 'management_company' || s.role === 'management_company')

  const [searchParams, setSearchParams] = useSearchParams()
  const tab: SectionKey = sectionFromParam(searchParams.get('tab')) ?? DEFAULT_TAB
  const tag = searchParams.get('tag')
  const requestedItemId = /^\d+$/.test(searchParams.get('item') ?? '') ? Number(searchParams.get('item')) : null
  const setParam = (key: 'tab' | 'tag' | 'item', value: string | null) => {
    const params = new URLSearchParams(searchParams)
    if (value === null || (key === 'tab' && value === DEFAULT_TAB)) params.delete(key)
    else params.set(key, value)
    setSearchParams(params, { replace: true })
  }
  const setTab = (next: SectionKey) => setParam('tab', next)

  const [closedDays, setClosedDays] = useState(30)
  const [search, setSearch] = useState('')
  const [openId, setOpenId] = useState<number | null>(null)
  const [requestOpen, setRequestOpen] = useState(false)
  const [editingRequest, setEditingRequest] = useState<WorkItemDetail | null>(null)
  const [newItemOpen, setNewItemOpen] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [moveError, setMoveError] = useState<string | null>(null)
  const [moving, setMoving] = useState(false)

  useEffect(() => {
    if (requestedItemId !== null) setOpenId(requestedItemId)
  }, [requestedItemId])

  const openItem = (itemId: number) => {
    setOpenId(itemId)
    setParam('item', String(itemId))
  }

  const closeItem = () => {
    setOpenId(null)
    setParam('item', null)
  }

  const closedSince = useMemo(() => {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    d.setDate(d.getDate() - closedDays)
    return d
  }, [closedDays])

  const itemsKey = ['work-items', closedDays]
  const { isSuperAdmin } = useSuperAdmin()
  const { data: isMember = false } = useQuery({ queryKey: ['work-board-member'], queryFn: isWorkBoardMember, enabled: isManagementCompany })
  const { data: members = [] } = useQuery({ queryKey: ['work-board-members'], queryFn: fetchWorkBoardMembers, enabled: isManagementCompany && isMember })
  const { data: directions = [] } = useQuery({ queryKey: ['work-directions'], queryFn: fetchWorkDirections, enabled: isManagementCompany })
  const { data: acceptors = [] } = useQuery({ queryKey: ['work-acceptors'], queryFn: fetchWorkAcceptors, enabled: isManagementCompany })
  const { data: items = [], isLoading, error: loadError } = useQuery({
    // С запасом в день: p_closed_from — дата UTC, а граница локальная.
    queryKey: itemsKey,
    queryFn: () => {
      const from = new Date(closedSince)
      from.setDate(from.getDate() - 1)
      return fetchWorkItems(toIsoDate(from))
    },
    enabled: isManagementCompany,
  })

  const activeMembers = useMemo(() => members.filter(m => m.is_active), [members])
  const sections = useMemo(() => buildBoardSections(items, { closedSince, query: search, direction: tag }), [items, closedSince, search, tag])
  /** Без фильтров: порядок блока сервер принимает только целиком. */
  const fullWork = useMemo(() => buildBoardSections(items, { closedSince }).find(s => s.key === 'work'), [items, closedSince])
  const activeSection = sections.find(s => s.key === tab)
  const tabTags = useMemo(() => {
    const all = buildBoardSections(items, { closedSince, query: search }).find(s => s.key === tab)?.items ?? []
    return directionTags(all)
  }, [items, closedSince, search, tab])
  const filtered = Boolean(tag) || search.trim().length > 0
  const invalidateBoard = () => void queryClient.invalidateQueries({ queryKey: ['work-items'] })

  /** Перестановка в блоке; ошибку бросает дальше — строка показывает её над списком, карточка у себя. */
  const applyMove = async (status: PriorityStatus, id: number, move: Move) => {
    const full = fullWork?.blocks?.find(b => b.status === status)
    const next = full ? moveInOrder(full.items.map(i => i.id), id, move) : null
    if (!next) return
    setMoving(true)
    // Сразу показываем новый порядок; при отказе сервера список перечитывается.
    queryClient.setQueryData<WorkItemRow[]>(itemsKey, old =>
      old?.map(i => (i.status === status && next.includes(i.id) ? { ...i, priority_rank: next.indexOf(i.id) + 1 } : i))
    )
    try {
      await reorderWorkItems(status, next, id)
    } finally {
      setMoving(false)
      invalidateBoard()
    }
  }

  const moveItem = async (block: BoardBlock, id: number, move: Move) => {
    setMoveError(null)
    try {
      await applyMove(block.status, id, move)
    } catch (e) {
      setMoveError(errorText(e, 'Не удалось поменять порядок'))
    }
  }

  if (!isManagementCompany) {
    return (
      <div className="container mx-auto max-w-2xl p-4">
        <Panel>Раздел доступен сотрудникам управляющей компании.</Panel>
      </div>
    )
  }

  const renderRows = (list: WorkItemRow[], block?: BoardBlock) =>
    list.map((item, index) => {
      const movable = block && isMember && !filtered
      return (
        <ItemRow
          key={item.id}
          item={item}
          onOpen={openItem}
          position={block ? index + 1 : undefined}
          onMove={movable ? move => void moveItem(block, item.id, move) : undefined}
          canMove={movable ? { up: index > 0, down: index < list.length - 1 } : undefined}
          moving={moving}
        />
      )
    })

  return (
    <div className="container mx-auto max-w-3xl space-y-4 p-4 pb-24">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-lg font-semibold text-foreground">Запросы в разработку</h1>
          <p className="text-xs text-muted-foreground">Что попросили у разработки, в каком порядке делаем и что ждёт приёмки.</p>
        </div>
        <div className="flex gap-2">
          {isMember && (
            <button type="button" className={GHOST_BUTTON} onClick={() => setNewItemOpen(true)}>
              <Plus size={12} /> Карточка
            </button>
          )}
          <button
            type="button"
            className={PRIMARY_BUTTON}
            onClick={() => {
              setEditingRequest(null)
              setRequestOpen(true)
            }}
          >
            <Send size={14} /> Попросить доработку
          </button>
        </div>
      </div>

      {notice && <Panel tone="success" size="sm">{notice}</Panel>}

      <input
        value={search}
        onChange={e => setSearch(e.target.value)}
        className={INPUT_CLASS}
        placeholder="Поиск: название, текст, кто просит, кто принимает"
        aria-label="Поиск"
      />

      {isLoading && <p className="text-sm text-muted-foreground">Загрузка…</p>}
      {loadError && <Panel tone="danger" size="sm">{errorText(loadError, 'Не удалось загрузить доску')}</Panel>}

      <div role="tablist" aria-label="Разделы доски" className="grid grid-cols-2 gap-1 rounded-md border border-border bg-card p-1 sm:grid-cols-5">
        {sections.map(s => {
          const active = s.key === tab
          return (
            <button
              key={s.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTab(s.key)}
              className={`rounded px-2 py-1.5 text-xs font-medium ${active ? 'bg-foreground text-background' : 'text-muted-foreground hover:bg-muted'}`}
            >
              {TAB_LABELS[s.key]} <span className={active ? '' : 'text-muted-foreground'}>· {s.items.length}</span>
            </button>
          )
        })}
      </div>

      {tabTags.length > 1 && (
        <div className="flex flex-wrap gap-1" aria-label="Теги">
          <button
            type="button"
            onClick={() => setParam('tag', null)}
            className={`rounded-full border px-2 py-0.5 text-xs ${!tag ? 'border-foreground text-foreground' : 'border-border text-muted-foreground hover:bg-muted'}`}
          >
            Все
          </button>
          {tabTags.map(t => (
            <button
              key={t.code}
              type="button"
              onClick={() => setParam('tag', tag === t.code ? null : t.code)}
              className={`rounded-full border px-2 py-0.5 text-xs ${directionColor(t.code)} ${tag === t.code ? 'font-semibold ring-2 ring-foreground/60' : 'opacity-80 hover:opacity-100'}`}
            >
              {t.title} · {t.count}
            </button>
          ))}
        </div>
      )}

      {activeSection && (
        <section className="space-y-2">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="text-xs text-muted-foreground">
              {activeSection.hint}
              {activeSection.key === 'work' && isMember && filtered && ' Чтобы менять порядок, сбросьте поиск и тег.'}
            </span>
            {activeSection.key === 'closed' && (
              <select
                value={closedDays}
                onChange={e => setClosedDays(Number(e.target.value))}
                className="h-7 rounded-md border border-border bg-background px-2 text-xs"
                aria-label="Срок закрытых"
              >
                {CLOSED_WINDOWS.map(w => (
                  <option key={w.days} value={w.days}>за {w.label}</option>
                ))}
              </select>
            )}
          </div>
          {moveError && <Panel tone="danger" size="sm">{moveError}</Panel>}
          {!isLoading && activeSection.items.length === 0 ? (
            <p className="text-xs text-muted-foreground">Пусто.</p>
          ) : activeSection.blocks ? (
            <div className="space-y-5">
              {activeSection.blocks.map(block => (
                <div key={block.status} className="space-y-2">
                  <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                    <StatusBadge status={block.status} />
                    <span>· {block.items.length}</span>
                    <span className="h-px flex-1 bg-border" />
                  </div>
                  {block.items.length === 0 ? <p className="text-xs text-muted-foreground">Пусто.</p> : renderRows(block.items, block)}
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-2">{renderRows(activeSection.items)}</div>
          )}
        </section>
      )}
      <RequestSheet
        open={requestOpen}
        editing={editingRequest}
        onClose={() => setRequestOpen(false)}
        directions={directions}
        acceptors={acceptors}
        briefOptional={isSuperAdmin === true}
        onSaved={(id, mode) => {
          setRequestOpen(false)
          setEditingRequest(null)
          invalidateBoard()
          void queryClient.invalidateQueries({ queryKey: ['work-item', id] })
          if (mode === 'created') {
            setNotice(`Запрос #${id} записан во вкладку «Новые».`)
            setTab('inbox')
          } else if (mode === 'resubmitted') {
            setNotice(`Запрос #${id} доработан и вернулся в «Новые».`)
          }
        }}
      />
      {isMember && (
        <NewItemSheet
          open={newItemOpen}
          onClose={() => setNewItemOpen(false)}
          members={activeMembers}
          directions={directions}
          onCreated={id => {
            setNewItemOpen(false)
            invalidateBoard()
            openItem(id)
          }}
        />
      )}
      <ItemSheet
        itemId={openId}
        isMember={isMember}
        members={activeMembers}
        directions={directions}
        onClose={closeItem}
        onChanged={invalidateBoard}
        onMoveTop={(status, id) => applyMove(status, id, 'top')}
        onEditRequest={detail => {
          closeItem()
          setEditingRequest(detail)
          setRequestOpen(true)
        }}
      />
    </div>
  )
}
